import { isDefined } from '@sgme/fp';
import { HStack } from '@sgme/ui';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { sendHelpRequestThunk } from '@/api/help-request/index.ts';
import type { SendHelpRequestRequest } from '@/api/help-request/sendHelpRequest.ts';
import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { useAlgoTileId } from '@/App/tiles/orders/AlgoOrders/AlgoContext.tsx';
import BackButton from '@/App/tiles/orders/AlgoOrders/AlgoOrderExecutedTile/BackButton.tsx';
import { logger } from '@/components/SgwtBootstrap/logger.ts';
import { getConfig } from '@/config/config.ts';
import { checkIfUserCanTrade, useUserContext } from '@/context/UserContext.tsx';
import { useAppDispatch } from '@/store/hooks.ts';
import { useCancelAlgoMutation } from '@/store/orders/algoOrders/api/index.ts';
import { ordersSlice } from '@/store/orders/slice.ts';
import type { AlgoOrderType } from '@/store/orders/state.ts';

export const ExecutedActionButtons = ({
  tradeId,
  algoType,
  currencyPair,
}: {
  tradeId: string | undefined;
  algoType: AlgoOrderType;
  currencyPair: string;
}) => {
  const track = useTrackEvent();

  const dispatch = useAppDispatch();

  const userContextValue = useUserContext();
  const readOnly = !checkIfUserCanTrade(userContextValue);

  const tileId = useAlgoTileId();

  const [cancelAlgo] = useCancelAlgoMutation({
    selectFromResult: () => ({}),
  });

  const [isCancelling, setIsCancelling] = useState(false);

  function handleCancelling() {
    setIsCancelling(true);
  }

  function stopCancelling() {
    setIsCancelling(false);
  }

  async function confirmCancel() {
    track('Order action', 'Cancel Order');
    if (isDefined(tradeId)) {
      cancelAlgo({ orderRef: tradeId, orderType: algoType })
        .unwrap()
        .catch((error) => {
          logger.logError('An error occured while cancelling order: {error_s}', error);
          const isTimeOut = error.status === 'TIMEOUT_ERROR' || error.status === 504;
          if (isTimeOut) {
            logger.logError('Order cancellation got timeout: {tradeId_s}', tradeId);
            const params: SendHelpRequestRequest = {
              config: 'orderpending',
              details: JSON.stringify({
                payload: error,
                orderIdToUpdate: tileId,
              }),
              data: {
                orderDescription: 'cancel',
                orderType: 'algo',
                /**
                 * send 'g10' or 'emerging' on order creation when we are able to differentiate
                 */
                orderCurrencyType: 'unknown',
                orderId: tileId,
              },
              templateId: getConfig().app.mailTemplateIds.templateIdForPendingOrder,
              templateIdConfirmation: getConfig().app.mailTemplateIds.templateIdForPendingOrderConfirmation,
            };

            logger.logError('Order submission failed on timeout for {tileId_s}', tileId);
            dispatch(sendHelpRequestThunk(params));
          }
          dispatch(
            ordersSlice.actions.orderFailed({
              tileId,
              errorMessage: isTimeOut ? 'order.timeout' : 'order.serverError',
            }),
          );
        });
    }
  }

  function editOrder() {
    track('Order action', 'Edit Order');
    dispatch(ordersSlice.actions.changeOrderMode({ mode: 'EDIT', tileId }));
  }

  return isCancelling ? (
    <HStack className="alert alert-info mt-4px" horizontalAlignment="center" data-e2e="cancel-confirm">
      <FormattedMessage id="order.form.actionButton.cancelling" />
      <button type="button" className="btn btn-white ms-auto" onClick={confirmCancel}>
        <FormattedMessage id="yes" />
      </button>
      <button type="button" className="btn btn-white ms-2" onClick={stopCancelling}>
        <FormattedMessage id="no" />
      </button>
    </HStack>
  ) : (
    <HStack horizontalAlignment="end" margin="8px" gap="8px" data-e2e="action-buttons-container">
      <BackButton currencyPair={currencyPair} readOnly={readOnly} />
      <button
        type="button"
        className="btn btn-md btn-outline-primary btn-icon-text gap-4px"
        onClick={editOrder}
        data-e2e="edit-btn"
        disabled={readOnly}
      >
        <em className="icon icon-sm"> edit</em>
        <FormattedMessage id="order.result.edit" />
      </button>
      <button
        type="button"
        className="btn btn-md btn-outline-danger btn-icon-text gap-4px"
        onClick={handleCancelling}
        data-e2e="cancel-btn"
        disabled={readOnly}
      >
        <em className="icon icon-sm"> cancel</em>
        <FormattedMessage id="order.result.cancelOrder" />
      </button>
    </HStack>
  );
};
